"use client";

import { usePathname, useRouter } from "@/i18n/navigation";
import { useSession } from "@/lib/auth/client";
import { ClientToServerEvents, ServerToClientEvents } from "@satranc365/shared";
import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { io, Socket } from "socket.io-client";

type ChessSocket = Socket<ServerToClientEvents, ClientToServerEvents>;
interface ChessSocketContextValue {
    socket: ChessSocket | null;
    isConnected: boolean;
    isPending: boolean;
    error: string | null;
}

const ChessSocketContext = createContext<ChessSocketContextValue | null>(null);

interface ChessSocketProviderProps {
    children: ReactNode;
}

export function ChessSocketProvider({ children }: ChessSocketProviderProps) {
    const { data: session, isPending: isSessionPending } = useSession();
    const router = useRouter();
    const pathname = usePathname();

    const socketRef = useRef<ChessSocket | null>(null);
    const [socketInstance, setSocketInstance] = useState<ChessSocket | null>(
        null,
    );

    const [isPending, setIsPending] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const authenticatedUserId = session?.user?.id;

    useEffect(() => {
        if (!authenticatedUserId) {
            if (!isSessionPending && pathname.startsWith("/oyun/")) {
                router.push(`/login?from=${encodeURIComponent(pathname)}`);
            }
            return;
        }

        const serverUrl = process.env.NEXT_PUBLIC_PVP_WS_URL;
        if (!serverUrl) {
            queueMicrotask(() => setError("Websocket url is not provided"));
            return;
        }

        queueMicrotask(() => setIsPending(true));
        let socket: ChessSocket | null = null;
        let cancelled = false;

        const onConnect = () => {
            setIsPending(false);
            setIsConnected(true);
            setError(null);
        };

        const onDisconnect = () => {
            setIsPending(false);
            setIsConnected(false);
        };

        const onError = (err: unknown) => {
            const message = err instanceof Error ? err.message : String(err);
            console.error("PvP socket connect_error:", message);
            setIsPending(false);
            setError(message);
            if (
                err instanceof Error &&
                err.message === "AUTH_ERROR" &&
                pathname.startsWith("/oyun/")
            ) {
                router.push(`/login?from=${encodeURIComponent(pathname)}`);
            }
        };

        const connectSocket = async () => {
            try {
                setIsPending(true);

                const response = await fetch("/api/pvp/token", {
                    method: "GET",
                    credentials: "include",
                    cache: "no-store",
                });

                if (!response.ok) {
                    throw new Error("PVP_AUTH_TOKEN_ERROR");
                }

                const data = (await response.json()) as { token?: string };

                if (!data.token) {
                    throw new Error("PVP_AUTH_TOKEN_ERROR");
                }

                if (cancelled) return;

                socket = io(serverUrl, {
                    auth: { sessionToken: data.token },
                    autoConnect: false,
                    transports: ["websocket"],
                });

                socketRef.current = socket;
                setSocketInstance(socket);

                socket.on("connect", onConnect);
                socket.on("disconnect", onDisconnect);
                socket.on("connect_error", onError);

                socket.connect();
            } catch (err) {
                if (cancelled) return;
                const message =
                    err instanceof Error ? err.message : String(err);
                setIsPending(false);
                setError(message);
            }
        };

        void connectSocket();

        return () => {
            cancelled = true;

            socket?.disconnect();

            socket?.off("connect", onConnect);
            socket?.off("disconnect", onDisconnect);
            socket?.off("connect_error", onError);

            socketRef.current = null;
            setSocketInstance(null);
        };
    }, [authenticatedUserId, isSessionPending, pathname, router]);

    const value = useMemo<ChessSocketContextValue>(
        () => ({
            socket: socketInstance,
            isConnected,
            isPending,
            error,
        }),
        [socketInstance, isConnected, isPending, error],
    );

    return (
        <ChessSocketContext.Provider value={value}>
            {children}
        </ChessSocketContext.Provider>
    );
}

export function useChessSocketContext(): ChessSocketContextValue {
    const context = useContext(ChessSocketContext);
    if (!context) {
        throw new Error(
            "useChessSocketContext must be used within a ChessSocketProvider",
        );
    }
    return context;
}
